import type { InstallationListRow } from '@/data/installations';
import type { ColumnHeaderOption } from '../../components/common/ColumnHeaderMenu';

export const INSTALLATION_LIBRARY_COLUMNS = {
  job: 'Job', client: 'Client', location: 'Installation location', stage: 'Stage',
  quote: 'Quote', hours: 'Billed hours', updated: 'Updated',
} as const;
export type InstallationLibraryColumn = keyof typeof INSTALLATION_LIBRARY_COLUMNS;
export type InstallationColumnFilters = Partial<Record<InstallationLibraryColumn, string[]>>;
export type InstallationLibrarySort = { column: InstallationLibraryColumn; direction: 'asc' | 'desc' };
type StageLabel = (row: InstallationListRow) => string;
const currency = (value: number) => value.toLocaleString(undefined, { style: 'currency', currency: 'USD' });

function columnValue(row: InstallationListRow, column: InstallationLibraryColumn, stageLabel: StageLabel): string {
  switch (column) {
    case 'job': return row.id;
    case 'client': return JSON.stringify([row.clientId, row.clientBranchId]);
    case 'location': return JSON.stringify([row.venueId, row.installationLocation]);
    case 'stage': return stageLabel(row);
    case 'quote': return row.total.toFixed(2);
    case 'hours': return row.billedHours.toFixed(1);
    case 'updated': return new Date(row.updatedAt).toLocaleDateString();
  }
}
function columnLabel(row: InstallationListRow, column: InstallationLibraryColumn, stageLabel: StageLabel): string {
  switch (column) {
    case 'job': return `${row.code} - ${row.name}`;
    case 'client': return [row.clientName, row.clientBranchName].filter(Boolean).join(' - ') || 'No client';
    case 'location': return [row.venueName, row.installationLocation].filter(Boolean).join(' - ') || 'No location';
    case 'quote': return currency(row.total);
    default: return columnValue(row, column, stageLabel);
  }
}

/** Facets use only the rows returned by the existing permission-scoped query. */
export function installationColumnOptions(rows: InstallationListRow[], stageLabel: StageLabel): Record<InstallationLibraryColumn, ColumnHeaderOption[]> {
  return Object.fromEntries((Object.keys(INSTALLATION_LIBRARY_COLUMNS) as InstallationLibraryColumn[]).map(column => {
    const values = new Map<string, ColumnHeaderOption>();
    rows.forEach(row => {
      const value = columnValue(row, column, stageLabel);
      const existing = values.get(value);
      values.set(value, { value, label: columnLabel(row, column, stageLabel), count: (existing?.count ?? 0) + 1 });
    });
    return [column, [...values.values()].sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }))];
  })) as Record<InstallationLibraryColumn, ColumnHeaderOption[]>;
}

export function filterAndSortInstallationRows(rows: InstallationListRow[], filters: InstallationColumnFilters, sort: InstallationLibrarySort, stageLabel: StageLabel): InstallationListRow[] {
  const filtered = rows.filter(row => (Object.entries(filters) as [InstallationLibraryColumn, string[]][])
    .every(([column, selected]) => selected.length === 0 || selected.includes(columnValue(row, column, stageLabel))));
  const sortable = (row: InstallationListRow): string | number => sort.column === 'quote' ? row.total
    : sort.column === 'hours' ? row.billedHours
      : sort.column === 'updated' ? new Date(row.updatedAt).getTime()
        : sort.column === 'job' ? row.code : columnLabel(row, sort.column, stageLabel);
  return filtered.sort((a, b) => {
    const left = sortable(a), right = sortable(b);
    const comparison = typeof left === 'number' && typeof right === 'number' ? left - right
      : String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: 'base' });
    return (sort.direction === 'asc' ? comparison : -comparison) || a.id.localeCompare(b.id);
  });
}
